import React, { useState, useEffect } from 'react';
import { FaTimes, FaQrcode, FaCheck, FaTimes as FaTimesCircle, FaUser, FaCalendarAlt, FaTicketAlt, FaCamera, FaKeyboard, FaArrowLeft, FaHistory, FaMapMarkerAlt } from 'react-icons/fa';
import { Scanner } from '@yudiel/react-qr-scanner';
import { Link } from 'react-router-dom';

import { validateTicket, markTicketAsUsedByCode } from '../../../components/http/Actions/TicketsAction';
import Notification from '../../../components/Notification';

const ScannerPage = () => {
    const [codigo, setCodigo] = useState('');
    const [validationResult, setValidationResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showQrReader, setShowQrReader] = useState(true); // Iniciamos con cámara activa
    const [qrError, setQrError] = useState('');
    const [notification, setNotification] = useState({ show: false, message: '', type: '' });
    const [ticketHistory, setTicketHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);

    const token = localStorage.getItem("token");

    // Función para mostrar notificación
    const showNotification = (message, type) => {
        setNotification({ show: true, message, type });
        setTimeout(() => {
            setNotification({ show: false, message: '', type: '' });
        }, 5000);
    };

    const handleValidate = async (ticketCode = null) => {
        const codeToValidate = ticketCode || codigo.trim();
        
        if (!codeToValidate) {
            setError('Ingresa un código de ticket');
            return;
        }

        setLoading(true);
        setError('');
        setQrError('');
        
        try {
            await validateTicket(
                codeToValidate,
                (result) => {
                    setValidationResult(result);
                    // Agregar al historial
                    const newEntry = {
                        id: Date.now(),
                        codigo: codeToValidate,
                        timestamp: new Date(),
                        valid: result.valid,
                        ticket: result.ticket,
                        message: result.message
                    };
                    setTicketHistory(prev => [newEntry, ...prev.slice(0, 9)]); // Mantener solo los últimos 10
                },
                setError,
                token
            );
            
            // Si se escaneó un QR, actualizar también el input
            if (ticketCode) {
                setCodigo(ticketCode);
            }
        } catch (error) {
            console.error('Error validating ticket:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUseTicket = async () => {
        if (!codigo.trim()) return;

        setLoading(true);
        try {
            await markTicketAsUsedByCode(codigo.trim(), setError, token);
            showNotification('✅ Ticket utilizado exitosamente', 'success');
            
            // Revalidar para mostrar el estado actualizado
            await validateTicket(
                codigo.trim(),
                setValidationResult,
                setError,
                token
            );

            // Auto-limpiar después de usar el ticket exitosamente
            setTimeout(() => {
                clearForm();
            }, 3000);
        } catch (error) {
            console.error('Error using ticket:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleQrScan = (result) => {
        if (result?.length > 0) {
            const scannedCode = result[0].rawValue;
            setQrError('');
            // Validar automáticamente el código escaneado
            handleValidate(scannedCode);
        }
    };

    const handleQrError = (error) => {
        console.error('QR Error:', error);
        setQrError('Error al acceder a la cámara. Verifica los permisos.');
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Fecha no disponible';
        return new Date(dateString).toLocaleDateString('es-CL', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0
        }).format(price);
    };

    const clearForm = () => {
        setCodigo('');
        setValidationResult(null);
        setError('');
        setQrError('');
    };

    const toggleQrReader = () => {
        setShowQrReader(!showQrReader);
        setQrError('');
        setError('');
        if (!showQrReader) {
            clearForm();
        }
    };

    // Efecto para reproducir sonidos (opcional)
    const playSound = (type) => {
        if (typeof Audio !== 'undefined') {
            try {
                const audio = new Audio();
                if (type === 'success') {
                    // Sonido de éxito (frecuencia alta)
                    audio.src = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBCGOzv';
                } else {
                    // Sonido de error (frecuencia baja)
                    audio.src = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBCGOzv';
                }
                audio.play().catch(() => {}); // Ignorar errores de reproducción
            } catch (e) {
                // Ignorar errores de audio
            }
        }
    };

    useEffect(() => {
        if (validationResult) {
            playSound(validationResult.valid ? 'success' : 'error');
        }
    }, [validationResult]);

    return (
        <div className="min-h-screen bg-zinc-900">
            {/* Header */}
            <header className="bg-zinc-800 shadow-lg">
                <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                    {/* ✅ Layout responsive mejorado */}
                    <div className="flex flex-col space-y-4 sm:flex-row sm:justify-between sm:items-center sm:space-y-0">
                        {/* Título y botón de volver */}
                        <div className="flex items-center">
                            <Link to="/admin" className="mr-4">
                                <FaArrowLeft className="text-white hover:text-red-500 transition-colors text-xl" />
                            </Link>
                            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center">
                                <FaQrcode className="mr-3 text-red-500" />
                                Escáner de Tickets
                            </h1>
                        </div>
                        
                        {/* Botones de acción - Ahora debajo en móviles */}
                        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                            <button
                                onClick={() => setShowHistory(!showHistory)}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center justify-center w-full sm:w-auto"
                            >
                                <FaHistory className="mr-2" />
                                Historial ({ticketHistory.length})
                            </button>
                            <button
                                onClick={clearForm}
                                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors w-full sm:w-auto"
                            >
                                Limpiar
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Notification */}
            {notification.show && (
                <Notification message={notification.message} type={notification.type} />
            )}

            {/* Main content */}
            <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Panel principal de escáner */}
                    <div className="lg:col-span-2">
                        <div className="bg-zinc-800 rounded-xl shadow-2xl p-6 border border-zinc-700">
                            {/* Controles de método */}
                            <div className="mb-6">
                                <div className="flex gap-3 mb-4">
                                    <button
                                        onClick={toggleQrReader}
                                        className={`flex-1 py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center font-medium ${
                                            showQrReader 
                                                ? 'bg-red-600 text-white' 
                                                : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                                        }`}
                                    >
                                        <FaCamera className="mr-2" />
                                        {showQrReader ? 'Cámara Activa' : 'Activar Cámara'}
                                    </button>
                                    <button
                                        onClick={() => setShowQrReader(false)}
                                        className={`flex-1 py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center font-medium ${
                                            !showQrReader 
                                                ? 'bg-blue-600 text-white' 
                                                : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                                        }`}
                                    >
                                        <FaKeyboard className="mr-2" />
                                        Entrada Manual
                                    </button>
                                </div>

                                {/* Lector QR */}
                                {showQrReader && (
                                    <div className="mb-4">
                                        <div className="bg-zinc-900 p-4 rounded-lg">
                                            <div className="aspect-square max-w-md mx-auto overflow-hidden rounded-lg relative">
                                                <Scanner
                                                    onScan={handleQrScan}
                                                    onError={handleQrError}
                                                    constraints={{
                                                        facingMode: 'environment'
                                                    }}
                                                    styles={{
                                                        container: { width: '100%', height: '100%' },
                                                        video: { width: '100%', height: '100%', objectFit: 'cover' }
                                                    }}
                                                    components={{
                                                        audio: false,
                                                        finder: true
                                                    }}
                                                    allowMultiple={false}
                                                />
                                                {/* Overlay de estado */}
                                                {loading && (
                                                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                                                        <div className="text-white text-center">
                                                            <div className="w-8 h-8 border-4 border-t-4 border-gray-200 border-t-red-600 rounded-full animate-spin mx-auto mb-2"></div>
                                                            <p>Validando...</p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="text-center mt-3 space-y-1">
                                                <p className="text-gray-400 text-sm">
                                                    📱 Apunta la cámara hacia el código QR
                                                </p>
                                                <p className="text-gray-500 text-xs">
                                                    El ticket se validará automáticamente
                                                </p>
                                                <div className="flex justify-center items-center space-x-4 mt-2">
                                                    <div className="flex items-center text-green-400 text-xs">
                                                        <div className="w-2 h-2 bg-green-400 rounded-full mr-1"></div>
                                                        Cámara activa
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Input manual */}
                                {!showQrReader && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Código del Ticket
                                        </label>
                                        <div className="flex flex-col sm:flex-row gap-3">
                                            <input
                                                type="text"
                                                value={codigo}
                                                onChange={(e) => setCodigo(e.target.value.toUpperCase())}
                                                className="flex-1 px-4 py-3 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 font-mono text-lg"
                                                placeholder="Ej: ABC123XYZ0"
                                                onKeyPress={(e) => e.key === 'Enter' && handleValidate()}
                                                autoFocus
                                            />
                                            <button
                                                onClick={() => handleValidate()}
                                                disabled={loading || !codigo.trim()}
                                                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors font-medium w-full sm:w-auto"
                                            >
                                                {loading ? (
                                                    <div className="flex items-center justify-center">
                                                        <div className="w-4 h-4 border-2 border-t-2 border-gray-200 border-t-white rounded-full animate-spin mr-2"></div>
                                                        Validando
                                                    </div>
                                                ) : 'Validar'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Errores */}
                            {qrError && (
                                <div className="bg-orange-500/20 border border-orange-500 text-orange-100 px-4 py-3 rounded-lg mb-6">
                                    <p className="text-sm font-medium">⚠️ {qrError}</p>
                                    <p className="text-xs mt-1">
                                        Asegúrate de permitir el acceso a la cámara
                                    </p>
                                </div>
                            )}

                            {error && (
                                <div className="bg-red-500/20 border border-red-500 text-red-100 px-4 py-3 rounded-lg mb-6">
                                    <p className="text-sm font-medium">❌ {error}</p>
                                </div>
                            )}

                            {/* Resultado de validación - AMPLIADO */}
                            {validationResult && (
                                <div className={`rounded-lg p-6 border-2 ${
                                    validationResult.valid 
                                        ? 'bg-green-900/30 border-green-500' 
                                        : 'bg-red-900/30 border-red-500'
                                }`}>
                                    <div className="flex items-center mb-6">
                                        {validationResult.valid ? (
                                            <FaCheck className="text-green-400 text-3xl mr-3" />
                                        ) : (
                                            <FaTimesCircle className="text-red-400 text-3xl mr-3" />
                                        )}
                                        <div>
                                            <h4 className={`text-xl font-bold ${
                                                validationResult.valid ? 'text-green-300' : 'text-red-300'
                                            }`}>
                                                {validationResult.valid ? '✅ TICKET VÁLIDO' : '❌ TICKET INVÁLIDO'}
                                            </h4>
                                            <p className="text-sm text-gray-400">{validationResult.message}</p>
                                        </div>
                                    </div>

                                    {validationResult.ticket && (
                                        <div className="bg-zinc-700/50 rounded-lg p-6 space-y-6">
                                            {/* Información del evento */}
                                            <div className="border-b border-zinc-600 pb-6">
                                                <div className="flex items-center mb-4">
                                                    <FaTicketAlt className="text-red-500 mr-3 text-xl" />
                                                    <span className="font-bold text-white text-lg">Información del Evento</span>
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div className="bg-zinc-800/50 p-4 rounded-lg">
                                                        <span className="text-gray-400 text-sm">Evento:</span>
                                                        <div className="font-bold text-white text-lg mt-1">
                                                            {validationResult.ticket.evento.nombre}
                                                        </div>
                                                    </div>
                                                    <div className="bg-zinc-800/50 p-4 rounded-lg">
                                                        <span className="text-gray-400 text-sm">Fecha y Hora:</span>
                                                        <div className="font-medium text-white mt-1">
                                                            {formatDate(validationResult.ticket.evento.fecha)}
                                                        </div>
                                                    </div>
                                                    <div className="md:col-span-2 bg-zinc-800/50 p-4 rounded-lg">
                                                        <span className="text-gray-400 text-sm">Lugar:</span>
                                                        <div className="font-medium text-white mt-1 flex items-center">
                                                            <FaMapMarkerAlt className="text-red-400 mr-2" />
                                                            {validationResult.ticket.evento.lugar}
                                                        </div>
                                                    </div>
                                                    <div className="bg-zinc-800/50 p-4 rounded-lg">
                                                        <span className="text-gray-400 text-sm">Estado del Evento:</span>
                                                        <div className="mt-1">
                                                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                                                validationResult.ticket.evento.habilitado 
                                                                    ? 'bg-green-600 text-white' 
                                                                    : 'bg-red-600 text-white'
                                                            }`}>
                                                                {validationResult.ticket.evento.habilitado ? 'Habilitado' : 'Deshabilitado'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Información del ticket */}
                                            <div className="border-b border-zinc-600 pb-6">
                                                <div className="flex items-center mb-4">
                                                    <FaUser className="text-blue-500 mr-3 text-xl" />
                                                    <span className="font-bold text-white text-lg">Información del Ticket</span>
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div className="bg-zinc-800/50 p-4 rounded-lg">
                                                        <span className="text-gray-400 text-sm">Usuario:</span>
                                                        <div className="font-medium text-white mt-1">
                                                            {validationResult.ticket.usuario?.nombre} {validationResult.ticket.usuario?.apellido}
                                                        </div>
                                                        <div className="text-sm text-gray-400 mt-1">
                                                            {validationResult.ticket.usuario?.email}
                                                        </div>
                                                    </div>
                                                    <div className="bg-zinc-800/50 p-4 rounded-lg">
                                                        <span className="text-gray-400 text-sm">Sección:</span>
                                                        <div className="font-medium text-white mt-1">
                                                            {validationResult.ticket.seccionEvento?.nombreSeccion}
                                                        </div>
                                                    </div>
                                                    <div className="bg-zinc-800/50 p-4 rounded-lg">
                                                        <span className="text-gray-400 text-sm">Precio:</span>
                                                        <div className="font-bold text-green-400 text-lg mt-1">
                                                            {formatPrice(validationResult.ticket.precio)}
                                                        </div>
                                                    </div>
                                                    <div className="bg-zinc-800/50 p-4 rounded-lg">
                                                        <span className="text-gray-400 text-sm">Estado del Ticket:</span>
                                                        <div className="mt-1">
                                                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                                                validationResult.ticket.estadoTicket === 'Activo' ? 'bg-green-600 text-white' :
                                                                validationResult.ticket.estadoTicket === 'Usado' ? 'bg-blue-600 text-white' :
                                                                'bg-red-600 text-white'
                                                            }`}>
                                                                {validationResult.ticket.estadoTicket}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="md:col-span-2 bg-zinc-800/50 p-4 rounded-lg">
                                                        <span className="text-gray-400 text-sm">Código del Ticket:</span>
                                                        <div className="font-mono text-lg bg-zinc-900 px-3 py-2 rounded mt-2 inline-block border-2 border-dashed border-yellow-400">
                                                            {validationResult.ticket.codigoTicket}
                                                        </div>
                                                    </div>
                                                    {validationResult.ticket.fechaUso && (
                                                        <div className="md:col-span-2 bg-zinc-800/50 p-4 rounded-lg">
                                                            <span className="text-gray-400 text-sm">Fecha de uso:</span>
                                                            <div className="font-medium text-blue-300 mt-1">
                                                                {formatDate(validationResult.ticket.fechaUso)}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Botón para usar ticket */}
                                            {validationResult.valid && validationResult.ticket.estadoTicket === 'Activo' && (
                                                <div className="pt-2">
                                                    <button
                                                        onClick={handleUseTicket}
                                                        disabled={loading}
                                                        className="w-full bg-green-600 hover:bg-green-700 text-white px-6 py-4 rounded-lg transition-colors disabled:opacity-50 font-bold text-lg flex items-center justify-center"
                                                    >
                                                        <FaCheck className="mr-2" />
                                                        {loading ? 'PROCESANDO...' : 'AUTORIZAR ENTRADA'}
                                                    </button>
                                                </div>
                                            )}

                                            {/* Mensajes de estado */}
                                            {validationResult.ticket.estadoTicket === 'Usado' && (
                                                <div className="bg-blue-900/50 border border-blue-500 rounded-lg p-4 text-center">
                                                    <span className="text-blue-200 font-medium text-lg">
                                                        ✓ ENTRADA YA UTILIZADA
                                                    </span>
                                                    {validationResult.ticket.fechaUso && (
                                                        <p className="text-blue-300 text-sm mt-1">
                                                            Usado el {formatDate(validationResult.ticket.fechaUso)}
                                                        </p>
                                                    )}
                                                </div>
                                            )}

                                            {/* Mensaje si el evento está deshabilitado */}
                                            {!validationResult.ticket.evento.habilitado && (
                                                <div className="bg-red-900/50 border border-red-500 rounded-lg p-4 text-center">
                                                    <span className="text-red-200 font-medium text-lg">
                                                        ⚠️ EL EVENTO ESTÁ DESHABILITADO
                                                    </span>
                                                    <p className="text-red-300 text-sm mt-1">
                                                        No se puede autorizar la entrada
                                                    </p>
                                                </div>
                                            )}

                                            {/* Validar otro ticket */}
                                            <div className="pt-4 border-t border-zinc-600">
                                                <button
                                                    onClick={clearForm}
                                                    className="w-full bg-zinc-600 hover:bg-zinc-700 text-white px-4 py-3 rounded-lg transition-colors font-medium"
                                                >
                                                    Validar otro ticket
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Panel lateral - Historial */}
                    <div className="lg:col-span-1">
                        <div className="bg-zinc-800 rounded-xl shadow-2xl border border-zinc-700">
                            <div className="p-4 border-b border-zinc-700">
                                <h3 className="text-lg font-bold text-white flex items-center">
                                    <FaHistory className="mr-2 text-blue-500" />
                                    Historial Reciente
                                </h3>
                            </div>
                            <div className="max-h-96 overflow-y-auto">
                                {ticketHistory.length === 0 ? (
                                    <div className="p-4 text-center text-gray-400">
                                        <FaTicketAlt className="text-3xl mx-auto mb-2 opacity-50" />
                                        <p className="text-sm">No hay tickets escaneados</p>
                                    </div>
                                ) : (
                                    <div className="p-2 space-y-2">
                                        {ticketHistory.map((entry) => (
                                            <div key={entry.id} className={`p-3 rounded-lg border ${
                                                entry.valid 
                                                    ? 'bg-green-900/20 border-green-600/30' 
                                                    : 'bg-red-900/20 border-red-600/30'
                                            }`}>
                                                <div className="flex items-center justify-between mb-1">
                                                    {entry.valid ? (
                                                        <FaCheck className="text-green-400 text-sm" />
                                                    ) : (
                                                        <FaTimesCircle className="text-red-400 text-sm" />
                                                    )}
                                                    <span className="text-xs text-gray-400">
                                                        {entry.timestamp.toLocaleTimeString('es-CL')}
                                                    </span>
                                                </div>
                                                <p className="text-xs font-mono text-gray-300 mb-1">
                                                    {entry.codigo}
                                                </p>
                                                {entry.ticket && (
                                                    <p className="text-xs text-gray-400 truncate">
                                                        {entry.ticket.usuario?.nombre} - {entry.ticket.evento?.nombre}
                                                    </p>
                                                )}
                                                <p className={`text-xs ${entry.valid ? 'text-green-300' : 'text-red-300'}`}>
                                                    {entry.message}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Estadísticas del día */}
                        <div className="bg-zinc-800 rounded-xl shadow-2xl border border-zinc-700 mt-6 p-4">
                            <h3 className="text-lg font-bold text-white mb-3">Estadísticas</h3>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-gray-400 text-sm">Total escaneados:</span>
                                    <span className="text-white font-medium">{ticketHistory.length}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-400 text-sm">Válidos:</span>
                                    <span className="text-green-400 font-medium">
{ticketHistory.filter(t => t.valid).length}
                                   </span>
                               </div>
                               <div className="flex justify-between">
                                   <span className="text-gray-400 text-sm">Inválidos:</span>
                                   <span className="text-red-400 font-medium">
                                       {ticketHistory.filter(t => !t.valid).length}
                                   </span>
                               </div>
                               <div className="flex justify-between">
                                   <span className="text-gray-400 text-sm">Utilizados:</span>
                                   <span className="text-blue-400 font-medium">
                                       {ticketHistory.filter(t => t.valid && t.ticket?.estadoTicket === 'Usado').length}
                                   </span>
                               </div>
                           </div>
                       </div>

                       {/* Información adicional */}
                       <div className="bg-zinc-800 rounded-xl shadow-2xl border border-zinc-700 mt-6 p-4">
                           <h3 className="text-lg font-bold text-white mb-3">Información</h3>
                           <div className="space-y-3 text-sm text-gray-300">
                               <div className="flex items-start">
                                   <FaCheck className="text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                                   <span>Los tickets válidos se pueden autorizar inmediatamente</span>
                               </div>
                               <div className="flex items-start">
                                   <FaTimesCircle className="text-red-400 mr-2 mt-0.5 flex-shrink-0" />
                                   <span>Los tickets inválidos o ya utilizados no permiten entrada</span>
                               </div>
                               <div className="flex items-start">
                                   <FaTicketAlt className="text-blue-400 mr-2 mt-0.5 flex-shrink-0" />
                                   <span>El historial se mantiene durante toda la sesión</span>
                               </div>
                               <div className="flex items-start">
                                   <FaCalendarAlt className="text-yellow-400 mr-2 mt-0.5 flex-shrink-0" />
                                   <span>Verifica siempre que el evento esté habilitado</span>
                               </div>
                           </div>
                       </div>

                       {/* Acciones rápidas */}
                       <div className="bg-zinc-800 rounded-xl shadow-2xl border border-zinc-700 mt-6 p-4">
                           <h3 className="text-lg font-bold text-white mb-3">Acciones Rápidas</h3>
                           <div className="space-y-2">
                               <button
                                   onClick={() => setShowQrReader(true)}
                                   className={`w-full py-2 px-3 rounded-lg text-sm transition-colors ${
                                       showQrReader 
                                           ? 'bg-red-600 text-white' 
                                           : 'bg-green-600 hover:bg-green-700 text-white'
                                   }`}
                               >
                                   {showQrReader ? 'Cámara Activa' : 'Activar Cámara'}
                               </button>
                               <button
                                   onClick={() => setShowQrReader(false)}
                                   className={`w-full py-2 px-3 rounded-lg text-sm transition-colors ${
                                       !showQrReader 
                                           ? 'bg-blue-600 text-white' 
                                           : 'bg-zinc-600 hover:bg-zinc-700 text-white'
                                   }`}
                               >
                                   Entrada Manual
                               </button>
                               <button
                                   onClick={clearForm}
                                   className="w-full py-2 px-3 rounded-lg text-sm bg-gray-600 hover:bg-gray-700 text-white transition-colors"
                               >
                                   Limpiar Todo
                               </button>
                               <button
                                   onClick={() => setTicketHistory([])}
                                   className="w-full py-2 px-3 rounded-lg text-sm bg-orange-600 hover:bg-orange-700 text-white transition-colors"
                               >
                                   Limpiar Historial
                               </button>
                           </div>
                       </div>

                       {/* Estado de conexión */}
                       <div className="bg-zinc-800 rounded-xl shadow-2xl border border-zinc-700 mt-6 p-4">
                           <h3 className="text-lg font-bold text-white mb-3">Estado del Sistema</h3>
                           <div className="space-y-2 text-xs">
                               <div className="flex justify-between items-center">
                                   <span className="text-gray-400">Conexión:</span>
                                   <span className="text-green-400 flex items-center">
                                       <div className="w-2 h-2 bg-green-400 rounded-full mr-1"></div>
                                       Online
                                   </span>
                               </div>
                               <div className="flex justify-between items-center">
                                   <span className="text-gray-400">Token:</span>
                                   <span className={token ? 'text-green-400' : 'text-red-400'}>
                                       {token ? 'Válido' : 'No disponible'}
                                   </span>
                               </div>
                               <div className="flex justify-between items-center">
                                   <span className="text-gray-400">Cámara:</span>
                                   <span className={showQrReader ? 'text-green-400' : 'text-gray-400'}>
                                       {showQrReader ? 'Activa' : 'Inactiva'}
                                   </span>
                               </div>
                               <div className="flex justify-between items-center">
                                   <span className="text-gray-400">Sesión iniciada:</span>
                                   <span className="text-gray-300 text-xs">
                                       {new Date().toLocaleTimeString('es-CL')}
                                   </span>
                               </div>
                           </div>
                       </div>
                   </div>
               </div>
           </main>
       </div>
   );
};

export default ScannerPage;